import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, LOGO_URL, BRAND } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, Phone, EnvelopeSimple, ShieldCheck, Star, ArrowUpRight, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PublicLanding() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/public/landing/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (data) {
      document.title = `${data.seo_title || data.headline} · True North Maintenance`;
      const meta = document.querySelector('meta[name="description"]') || document.createElement("meta");
      meta.setAttribute("name", "description");
      meta.setAttribute("content", data.seo_description || "");
      document.head.appendChild(meta);
    }
  }, [data]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Name and email required");
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, service: form.service || data?.service_focus || "general", source: `landing:${slug}` });
      setSubmitted(true);
    } catch {
      toast.error("Could not submit. Try again.");
    } finally { setSubmitting(false); }
  };

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center bg-cream p-6">
      <div className="text-center">
        <img src={LOGO_URL} alt="" className="w-16 mx-auto bg-navy-deep p-2 rounded-sm"/>
        <h1 className="mt-4 font-display text-3xl tracking-wide text-navy">Page not found</h1>
        <p className="text-slate-600 mt-2">This landing page no longer exists.</p>
        <a href="/" className="mt-4 inline-block pill-navy">Back to home <ArrowUpRight size={14} weight="bold"/></a>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;

  return (
    <div data-testid="public-landing" className="min-h-screen bg-paper">
      <header className="border-b border-slate-200 bg-paper sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt={BRAND.name} className="h-9 invert opacity-90" />
            <span className="hidden sm:block font-display tracking-wider text-navy text-sm">TRUE NORTH MAINTENANCE</span>
          </a>
          <a href="#quote" className="text-xs uppercase tracking-[0.22em] font-medium text-navy hover:text-navy-deep inline-flex items-center gap-1">Get a quote <ArrowUpRight size={12} weight="bold"/></a>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-7">
          <div className="eyebrow eyebrow-dark">Trusted local property maintenance · {BRAND.area}</div>
          <h1 className="font-display display-xl tracking-wide text-navy mt-5 leading-[0.92]">{data.headline}</h1>
          <p className="mt-6 text-lg text-slate-700 font-light leading-relaxed">{data.subheadline}</p>

          <div className="mt-7 flex flex-wrap gap-3 items-center">
            <a href="#quote" className="pill-navy">{data.cta} <ArrowUpRight size={14} weight="bold"/></a>
            <a href={`tel:${BRAND.phone.replace(/\s/g,'')}`} className="inline-flex items-center gap-2 text-sm font-medium text-navy hover:text-navy-deep"><Phone size={14} weight="fill"/>{BRAND.phone}</a>
          </div>

          <div className="mt-10 grid grid-cols-2 gap-4">
            {(data.benefits || []).map((b) => (
              <div key={b} className="flex items-start gap-2.5 text-sm text-slate-800">
                <CheckCircle size={18} weight="fill" className="text-navy mt-0.5 shrink-0" /><span>{b}</span>
              </div>
            ))}
          </div>

          <div className="mt-10 flex items-center gap-5 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} weight="fill" className="text-navy"/>Licensed & insured</span>
            <span className="inline-flex items-center gap-1.5"><Star size={14} weight="fill" className="text-navy"/>4.9 / 5 local reputation</span>
          </div>

          <blockquote className="mt-12 border-l-2 border-navy pl-5 italic text-slate-700 text-lg font-light">
            "{data.testimonial_quote}"
            <div className="mt-2 not-italic text-[10px] uppercase tracking-[0.22em] text-slate-400 font-medium">— Recent customer</div>
          </blockquote>
        </div>

        <div id="quote" className="lg:col-span-5 bg-navy-deep text-white p-6 md:p-8 rounded-sm lg:sticky lg:top-24">
          <div className="eyebrow !text-white/60 mb-2">Free quote</div>
          <h2 className="font-display text-3xl md:text-4xl tracking-wide mb-1">Tell us about the job</h2>
          <p className="text-white/60 text-sm mb-6 font-light">We'll get back to you shortly.</p>
          {submitted ? (
            <div className="text-center py-10">
              <CheckCircle size={40} weight="fill" className="mx-auto text-white" />
              <h3 className="mt-4 font-display text-2xl tracking-wide">Thanks, {form.name.split(" ")[0]}.</h3>
              <p className="text-white/70 text-sm mt-2 font-light">We've received your request and will reach out at {form.email} shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] font-medium text-white/60">Your name</label>
                <Input data-testid="lp-name" required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="rounded-sm mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30" placeholder="Jane Smith" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] font-medium text-white/60">Email</label>
                <Input data-testid="lp-email" type="email" required value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="rounded-sm mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30" placeholder="jane@example.com" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] font-medium text-white/60">Phone</label>
                <Input data-testid="lp-phone" value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} className="rounded-sm mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30" placeholder="(555) 123-4567" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] font-medium text-white/60">Service needed</label>
                <Input data-testid="lp-service" value={form.service} onChange={(e)=>setForm({...form, service: e.target.value})} placeholder={data.service_focus} className="rounded-sm mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] font-medium text-white/60">Message</label>
                <Textarea data-testid="lp-message" rows={3} value={form.message} onChange={(e)=>setForm({...form, message: e.target.value})} className="rounded-sm mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30" placeholder="Tell us about the job..." />
              </div>
              <Button data-testid="lp-submit" type="submit" disabled={submitting} className="w-full pill-light !bg-white !text-navy-deep justify-center !py-3.5 !text-sm mt-4">{submitting ? "Sending..." : data.cta}</Button>
              <p className="text-[10px] text-white/40 text-center pt-1">No spam. We only contact you about your request.</p>
            </form>
          )}
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8 text-center text-xs text-slate-500 bg-paper">
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <a href={`mailto:${BRAND.email}`} className="inline-flex items-center gap-1 hover:text-navy"><EnvelopeSimple size={12}/>{BRAND.email}</a>
          <span>·</span>
          <a href={`tel:${BRAND.phone.replace(/\s/g,'')}`} className="inline-flex items-center gap-1 hover:text-navy"><Phone size={12}/>{BRAND.phone}</a>
          <span>·</span>
          <span className="inline-flex items-center gap-1"><MapPin size={12}/>{BRAND.area}</span>
        </div>
        <div className="mt-2">© {new Date().getFullYear()} True North Maintenance. All rights reserved.</div>
      </footer>
    </div>
  );
}
